/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useRef } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { LandingPageHeaderH2 } from "~/components/landing/LandingText";
import VehicleMarker from "~/components/landing/bento/VehicleMarker";
import { useHasBeenOnScreen } from "~/lib/use-has-been-on-screen";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const visibilityForeground = imageUrl("/images/shoploaner/visibility-foreground.png");
const visibilityBackground = imageUrl("/images/shoploaner/visibility-background.jpg");

/**
 * Ported from bouncieweb's `LandingPageComponents/Visibility.js`.
 *
 * A blueLight8 panel: copy on the left, an animated street scene on the right
 * (stacked below lg, side-by-side from lg). Once the image is on screen the
 * oversized background slowly pans left-to-right over 15s, behind a translucent
 * blue "trip line", a pulsing arrow-less VehicleMarker, and a foreground card.
 *
 * The marker is positioned responsively, which inline styles can't express, so it
 * uses VehicleMarker's `className` rather than its `styles` prop.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   sm 640px -> sm:  md 768px -> md:  lg 1024px -> lg:
 */

const ANIMATION_STYLES = `
  @keyframes loaner-visibility-pan-base {
    0%   { transform: translateX(-59%); }
    100% { transform: translateX(0%); }
  }
  @keyframes loaner-visibility-pan-md {
    0%   { transform: translateX(-48%); }
    100% { transform: translateX(0%); }
  }

  .loaner-visibility-bg {
    transform: translateX(-59%);
  }
  .loaner-visibility-bg.loaner-visibility-animate {
    animation: loaner-visibility-pan-base 15s forwards 1 ease-out;
  }
  @media (min-width: 768px) {
    .loaner-visibility-bg { transform: translateX(-48%); }
    .loaner-visibility-bg.loaner-visibility-animate {
      animation: loaner-visibility-pan-md 15s forwards 1 ease-out;
    }
  }
`;

export default function Visibility({
  title,
  description,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
}) {
  const imageSectionRef = useRef<HTMLDivElement>(null);
  const imageSectionHasBeenOnScreen = useHasBeenOnScreen(imageSectionRef);

  return (
    <>
      <style>{ANIMATION_STYLES}</style>

      {/* Wrapper */}
      <div
        className="overflow-hidden rounded-[20px] text-left lg:flex lg:flex-row"
        style={{ background: colors.blueLight8 }}
      >
        {/* TextSection */}
        <div className="p-8 lg:flex-[0_0_50%] lg:p-12 lg:pb-0">
          {/* CustomLandingPageHeader */}
          <LandingPageHeaderH2
            className={cn(
              "mt-0 mb-6 text-left [text-wrap:wrap]",
              "text-[8.3vw] min-[431px]:text-[8.3vw] sm:text-[8.3vw] md:text-[8.3vw] lg:text-[41px]",
            )}
          >
            {title}
          </LandingPageHeaderH2>
          {/* CustomP */}
          <div className="m-0">{description}</div>
        </div>

        {/* ImageSection */}
        <div
          ref={imageSectionRef}
          className="h-[calc(66vw-30px)] overflow-hidden sm:h-[18.5rem] lg:h-[17.5rem] lg:flex-[0_0_50%]"
        >
          {/* AnimationWrapper */}
          <div className="relative aspect-square w-full">
            {/* Background — oversized, pans in once on screen */}
            <Picture
              src={visibilityBackground}
              alt="A city street"
              className={cn(
                "loaner-visibility-bg absolute h-fit max-w-none",
                "top-[-2%] left-0 w-[245%]",
                "md:top-[-3%] md:w-[195%] lg:w-[206%]",
                imageSectionHasBeenOnScreen && "loaner-visibility-animate",
              )}
            />

            {/* TripLine */}
            <div
              className={cn(
                "absolute left-1/2 h-[4%] w-1/2 opacity-50",
                "top-[39.4%] md:top-[29.75%] md:h-[3%] lg:top-[31.7%]",
              )}
              style={{ background: colors.blue }}
            />

            <VehicleMarker
              showArrow={false}
              size="35%"
              className={cn(
                "absolute left-1/2 -translate-x-1/2",
                "top-[23.5%] md:top-[13.5%] lg:top-[15%]",
              )}
            />

            {/* Foreground */}
            <Picture
              src={visibilityForeground}
              alt="A car with a notification over it showing the current time and location"
              className={cn(
                "absolute h-fit max-w-none",
                "top-[5%] left-[5%] w-[90%]",
                "md:top-[3%] md:left-[15%] md:w-[70%] lg:top-[5%]",
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
}
